import React, { useEffect, useState, useRef, useCallback } from "react";
import { DropdownMenu, DropdownItem } from "reactstrap";
import { INotification } from "../../../interfaces/notification";
import { X, Box, AlertTriangle, AlertCircle, Package, Globe } from "react-feather";
import moment from "moment";
import { Link } from "react-router-dom";
import ingredientIcon from "../../../assets/images/icon-nav-ingredients.svg";
import { IApplicationState } from "../../../redux/reducers";
import { IProduct } from "../../../interfaces/products";
import { IPaginateList } from "../../../interfaces/pagination";
import { connect } from "react-redux";
import productService from "../../../services/productService";
import { IBrand } from "../../../interfaces/company";
import userService from "../../../services/userService";
import { stat } from "fs";

interface IProps {
	selected_brand: IBrand;
	handleSelect: (brand: IBrand) => void;
	enablePagination?: boolean; // Optional prop to enable pagination
}

interface IBrandResponse {
	data: IBrand[];
	meta: {
		current_page: number;
		total_pages: number;
		total_items: number;
		per_page: number;
	};
}

const BrandDropdown: React.FC<IProps> = React.memo((props) => {
	const { enablePagination = false } = props;

	// Feature flag: Enable pagination via environment variable or prop
	const usePagination = enablePagination || process.env.REACT_APP_ENABLE_BRAND_PAGINATION === 'true';

	// Common state
	const [loading, setLoading] = useState<boolean>();
	const [brands, setBrands] = useState<IBrand[]>([]);

	// Pagination-specific state
	const [loadingMore, setLoadingMore] = useState<boolean>(false);
	const [currentPage, setCurrentPage] = useState<number>(1);
	const [hasMorePages, setHasMorePages] = useState<boolean>(true);
	const [totalPages, setTotalPages] = useState<number>(0);
	const [error, setError] = useState<string | null>(null);

	// Refs for pagination
	const dropdownRef = useRef<HTMLDivElement>(null);
	const sentinelRef = useRef<HTMLDivElement>(null);

	// Load brands function - handles both paginated and non-paginated
	const loadBrands = useCallback(async (page: number = 1, isInitial: boolean = true) => {
		if (isInitial) {
			setLoading(true);
		} else {
			setLoadingMore(true);
		}
		setError(null);

		try {
			let response: any;

			if (usePagination) {
				// Use paginated API
				const thunk = userService.getBrandsPaginated(page, 30);
				response = await thunk(
					(action: any) => action, // Mock dispatch
					() => ({} as any) // Mock getState
				);

				if (response && response.data && Array.isArray(response.data)) {
					if (isInitial) {
						setBrands(response.data);
					} else {
						setBrands(prevBrands => [...prevBrands, ...response.data]);
					}

					setCurrentPage(response.meta.current_page);
					setTotalPages(response.meta.total_pages);
					setHasMorePages(response.meta.current_page < response.meta.total_pages);
				} else {
					throw new Error('Invalid paginated response format');
				}
			} else {
				// Use original API (non-paginated)
				const thunk = userService.getBrands();
				response = await thunk(
					(action: any) => action, // Mock dispatch
					() => ({} as any) // Mock getState
				);


				// Ensure we always set an array
				if (Array.isArray(response)) {
					setBrands(response);
				} else {
					console.error('Brands response is not an array:', response);
					setBrands([]);
				}
			}
		} catch (error) {
			console.error('Error fetching brands:', error);
			setError('Failed to load brands');
			if (isInitial) {
				setBrands([]);
			}
		} finally {
			setLoading(false);
			setLoadingMore(false);
		}
	}, [usePagination]);

	// Load more brands for pagination
	const loadMoreBrands = useCallback(() => {
		if (hasMorePages && !loadingMore && !loading) {
			loadBrands(currentPage + 1, false);
		}
	}, [hasMorePages, loadingMore, loading, currentPage, loadBrands]);

	// Retry function for error handling
	const handleRetry = useCallback(() => {
		setError(null);
		loadBrands(currentPage, currentPage === 1);
	}, [currentPage, loadBrands]);

	// Initial load
	useEffect(() => {
		const token = localStorage.getItem("access_token");
		if (token) {
			loadBrands(1, true);
		} else {
			setLoading(false);
			setBrands([]);
		}
	}, [loadBrands]);

	// Intersection Observer for infinite scroll (only when pagination is enabled)
	useEffect(() => {
		if (!usePagination || !dropdownRef.current) return;

		let observer: IntersectionObserver | null = null;
		let scrollableElement: HTMLElement | null = null;
		let handleScroll: (() => void) | null = null;

		// Use a small delay to ensure DOM is ready
		const timeoutId = setTimeout(() => {
			// Find the scrollable dropdown menu element
			// The DropdownMenu component renders as .dropdown-menu
			scrollableElement = dropdownRef.current?.querySelector('.dropdown-menu.brand') as HTMLElement;

			if (!scrollableElement || !sentinelRef.current) return;

			observer = new IntersectionObserver(
				(entries) => {
					const [entry] = entries;
					if (entry.isIntersecting && hasMorePages && !loadingMore && !loading) {
						loadMoreBrands();
					}
				},
				{
					root: scrollableElement,
					rootMargin: '50px',
					threshold: 0.1
				}
			);

			observer.observe(sentinelRef.current);

			// Also add scroll event listener as a fallback for better reliability
			handleScroll = () => {
				if (!sentinelRef.current || !scrollableElement) return;

				const scrollBottom = scrollableElement.scrollTop + scrollableElement.clientHeight;
				const scrollHeight = scrollableElement.scrollHeight;

				// Check if user has scrolled near the bottom (within 100px)
				if (
					scrollHeight - scrollBottom < 100 &&
					hasMorePages &&
					!loadingMore &&
					!loading
				) {
					loadMoreBrands();
				}
			};

			scrollableElement.addEventListener('scroll', handleScroll, { passive: true });
		}, 100);

		return () => {
			clearTimeout(timeoutId);
			if (observer) {
				observer.disconnect();
			}
			if (scrollableElement && handleScroll) {
				scrollableElement.removeEventListener('scroll', handleScroll);
			}
		};
	}, [usePagination, hasMorePages, loadingMore, loading, loadMoreBrands]);

	return (
		<div className="header-notification-container" ref={dropdownRef}>
			<DropdownMenu className={"brand"}>
				<div className="dropdown__notifications">
					{/* All Brands Option */}
					<DropdownItem key={"All Brands"} onClick={() => props.handleSelect({ id: "" })}>
						<div className="brand dropdown__notification color-battleship-grey">
							<div className="notification__detail">
								<span className="notification__title">
									<Globe className={"color-bg-slate brand-icon"} height={24} /> All Brands
								</span>
							</div>
						</div>
					</DropdownItem>

					{/* Loading State for Initial Load */}
					{loading && brands.length === 0 && (
						<DropdownItem disabled>
							<div className="brand dropdown__notification color-battleship-grey">
								<div className="notification__detail">
									<span className="notification__title">
										Loading brands...
									</span>
								</div>
							</div>
						</DropdownItem>
					)}

					{/* Error State (only for pagination) */}
					{usePagination && error && (
						<DropdownItem onClick={handleRetry}>
							<div className="brand dropdown__notification color-battleship-grey">
								<div className="notification__detail">
									<span className="notification__title" style={{ color: '#dc3545' }}>
										{error} - Click to retry
									</span>
								</div>
							</div>
						</DropdownItem>
					)}

					{/* Brand List */}
					{Array.isArray(brands) && brands.map((brand) => (
						<DropdownItem key={brand.id} onClick={() => props.handleSelect(brand)}>
							<div className="brand dropdown__notification color-battleship-grey">
								<div className="notification__detail">
									<span className="notification__title">
										<Package
											className={"brand-icon"}
											height={24}
											color={"#fff"}
											style={{ backgroundColor: "#3f65f1", padding: 5, borderRadius: "50%" }}
										/>
										{brand.name}
									</span>
								</div>
							</div>
						</DropdownItem>
					))}

					{/* Loading More Indicator (only for pagination) */}
					{usePagination && loadingMore && (
						<DropdownItem disabled>
							<div className="brand dropdown__notification color-battleship-grey">
								<div className="notification__detail">
									<span className="notification__title">
										Loading more brands...
									</span>
								</div>
							</div>
						</DropdownItem>
					)}

					{/* Sentinel Element for Intersection Observer (only for pagination) */}
					{usePagination && <div ref={sentinelRef} style={{ height: '1px', width: '100%' }} />}
				</div>
			</DropdownMenu>
		</div>
	);
});

const mapStateToProps = (state: IApplicationState) => ({
	selected_brand: state.user.selected_brand
});

export default connect(mapStateToProps)(BrandDropdown);